import { useEffect, useState } from "react";
import { CONTENT } from "../content/events";
import { currentStage, startChildLife, startLife } from "../engine/life";
import { randomSeed } from "../engine/rng";
import type { LifeState } from "../engine/types";
import { advanceTrack } from "../geo/locate";
import { SEED_PATTERN, type SpawnResult } from "../shared/api";
import { recordBirth, recordLife, type Dex as DexData } from "../shared/dex";
import { unlockAfterLife, type AchievementDef, type AchievementState } from "../shared/achievements";
import { countryAt } from "../geo/geodata";
import { fetchSharedSpecies, fetchSpawn, reportLife } from "./api";
import { lifeReport } from "../shared/stats";
import { rangeOf, useGeo } from "./geo";
import { withLocalWildlife } from "./neighbors";
import { Achievements } from "./screens/Achievements";
import { GlobalStatsScreen } from "./screens/GlobalStats";
import { Dex } from "./screens/Dex";
import { Home } from "./screens/Home";
import { Life } from "./screens/Life";
import { Reveal } from "./screens/Reveal";
import { SpeciesCardView } from "./screens/SpeciesCardView";
import { Summary } from "./screens/Summary";
import { clearSaved, loadAchievements, loadDex, loadSaved, save, saveAchievements, saveDex, type SavedLife } from "./storage";

type Screen =
  | { name: "home" }
  | { name: "reveal"; spawn: SpawnResult }
  | { name: "card"; spawn: SpawnResult }
  | { name: "life"; spawn: SpawnResult; life: LifeState }
  | { name: "summary"; spawn: SpawnResult; life: LifeState; dexNote: string | null; unlocked: AchievementDef[]; sent: Promise<void> }
  | { name: "dex" }
  | { name: "achievements" }
  | { name: "stats" };

function sharedFromUrl(): { id: number; seed: string } | null {
  const params = new URLSearchParams(location.search);
  const id = Number(params.get("species"));
  const seed = params.get("seed") ?? "";
  return Number.isInteger(id) && id !== 0 && SEED_PATTERN.test(seed) ? { id, seed } : null;
}

function clearUrl() {
  if (location.search) history.replaceState(null, "", "/");
}

export function App() {
  const [screen, setScreen] = useState<Screen>({ name: "home" });
  const [saved, setSaved] = useState<SavedLife | null>(() => loadSaved());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dex, setDex] = useState<DexData>(() => loadDex());
  const [achievements, setAchievements] = useState<AchievementState>(() => loadAchievements());

  function storeDex(next: DexData) {
    setDex(next);
    saveDex(next);
  }

  /** จบชีวิต: เก็บสถิติลงสมุดสัตว์ แล้วบอกว่าได้ชนิดใหม่หรือทำสถิติใหม่ไหม */
  function finish(spawn: SpawnResult, life: LifeState) {
    const key = String(life.species.id);
    const before = dex.entries[key];
    const next = recordLife(dex, life);
    const after = next.entries[key];
    storeDex(next);
    const report = lifeReport(life);
    const sent = report ? reportLife(report) : Promise.resolve();
    const birth = life.geo?.birth;
    const bornCountry = grid && birth && !birth.onSea ? countryAt(grid, birth.cell) : null;
    const { state: got, newly } = unlockAfterLife(achievements, { life, dex: next, bornCountry });
    if (newly.length) {
      setAchievements(got);
      saveAchievements(got);
    }
    const name = life.species.nameTh ?? life.species.sci;
    let note: string | null = null;
    if (after.lives === 1) note = `เพิ่ม${name}ลงสมุดสัตว์แล้ว ตอนนี้สะสมได้ ${Object.keys(next.entries).length.toLocaleString("th-TH")} ชนิด`;
    else if (before && after.bestAgeDays > before.bestAgeDays) note = `สถิติใหม่! ชีวิตนี้เป็น${name}ที่อายุยืนที่สุดที่คุณเคยเล่น`;
    else if (before && after.bestOffspring > before.bestOffspring) note = `สถิติใหม่! ชีวิตนี้เป็น${name}ที่มีลูกมากที่สุดที่คุณเคยเล่น`;
    clearSaved();
    setScreen({ name: "summary", spawn, life, dexNote: note, unlocked: newly, sent });
  }

  /** เกิดเป็นชนิดที่เคยเจอในสมุดอีกครั้ง ด้วย seed ใหม่ */
  async function playAgain(id: number) {
    setBusy(true);
    setError(null);
    try {
      setScreen({ name: "card", spawn: await fetchSharedSpecies(id, randomSeed()) });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  // โหลดแผนที่ไว้ก่อนตั้งแต่เปิดหน้า จะได้พร้อมตอนเปิดการ์ดสัตว์
  const geo = useGeo();
  const grid = geo.status === "ready" ? geo.data.grid : null;
  const rangeFor = (spawn: SpawnResult) => (grid ? rangeOf(spawn, grid) : null);

  /** ย้ายตำแหน่งหลังจบแต่ละ turn แล้วดูว่าแถวนี้มีนักล่าและเหยื่อชนิดไหน (ถ้าแผนที่พร้อมและชนิดนี้มีถิ่นที่อยู่) */
  function placed(spawn: SpawnResult, life: LifeState): LifeState {
    const range = rangeFor(spawn);
    if (!grid || !range) return life;
    const moved = advanceTrack(life, range, grid);
    return withLocalWildlife(moved, currentStage(moved));
  }

  // เปิดจากลิงก์แชร์: ไปที่การ์ดสัตว์ตัวนั้นด้วย seed เดิม
  useEffect(() => {
    const shared = sharedFromUrl();
    if (!shared) return;
    setBusy(true);
    fetchSharedSpecies(shared.id, shared.seed)
      .then((spawn) => setScreen({ name: "card", spawn }))
      .catch((e: Error) => {
        setError(e.message);
        clearUrl();
      })
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [screen.name]);

  async function newLife() {
    setBusy(true);
    setError(null);
    clearUrl();
    try {
      const spawn = await fetchSpawn(randomSeed());
      setScreen({ name: "reveal", spawn });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function begin(spawn: SpawnResult) {
    const life = placed(spawn, startLife(spawn.species, spawn.seed, CONTENT));
    storeDex(recordBirth(dex, life).dex);
    save({ spawn, life });
    setScreen({ name: "life", spawn, life });
  }

  /** เล่นต่อเป็นลูกของชีวิตที่เพิ่งจบ */
  function continueAsChild(spawn: SpawnResult, parent: LifeState) {
    clearUrl();
    const life = placed(spawn, startChildLife(parent, CONTENT));
    storeDex(recordBirth(dex, life).dex);
    save({ spawn, life });
    setScreen({ name: "life", spawn, life });
  }

  function update(spawn: SpawnResult, life: LifeState) {
    const entry = { spawn, life };
    save(entry);
    setSaved(life.alive ? entry : null);
    setScreen({ name: "life", spawn, life });
  }

  function goHome() {
    clearUrl();
    setSaved(loadSaved());
    setScreen({ name: "home" });
  }

  switch (screen.name) {
    case "home":
      return (
        <Home
          saved={saved}
          busy={busy}
          error={error}
          onNewLife={newLife}
          onResume={() => saved && setScreen({ name: "life", spawn: saved.spawn, life: saved.life })}
          dexCount={Object.keys(dex.entries).length}
          achievementCount={Object.keys(achievements.unlocked).length}
          onAchievements={() => setScreen({ name: "achievements" })}
          onStats={() => setScreen({ name: "stats" })}
          onDex={() => {
            setError(null);
            setScreen({ name: "dex" });
          }}
        />
      );
    case "reveal":
      return <Reveal spawn={screen.spawn} onContinue={() => setScreen({ name: "card", spawn: screen.spawn })} />;
    case "card":
      return (
        <SpeciesCardView spawn={screen.spawn} geo={geo} range={rangeFor(screen.spawn)} onStart={() => begin(screen.spawn)} onBack={goHome} />
      );
    case "life":
      return (
        <Life
          life={screen.life}
          spawn={screen.spawn}
          geo={geo}
          range={rangeFor(screen.spawn)}
          advance={(life) => placed(screen.spawn, life)}
          onChange={(life) => update(screen.spawn, life)}
          onFinished={() => finish(screen.spawn, screen.life)}
        />
      );
    case "summary":
      return (
        <Summary
          life={screen.life}
          spawn={screen.spawn}
          geo={geo}
          range={rangeFor(screen.spawn)}
          onNewLife={newLife}
          onContinue={() => continueAsChild(screen.spawn, screen.life)}
          dexNote={screen.dexNote}
          unlocked={screen.unlocked}
          sent={screen.sent}
          onAchievements={() => setScreen({ name: "achievements" })}
          onDex={() => setScreen({ name: "dex" })}
        />
      );
    case "stats":
      return <GlobalStatsScreen onBack={goHome} />;
    case "achievements":
      return <Achievements achievements={achievements} dex={dex} onBack={goHome} />;
    case "dex":
      return <Dex dex={dex} busy={busy} error={error} onPlay={playAgain} onBack={goHome} />;
  }
}
